import { loadAptsCache, saveAllApts } from "./aptStore";

// ics.js — iCalendar-Parser (RFC 5545) für den Termin-Import.
// Deckt das ab, was Outlook/Google/Apple beim Kalender-Export erzeugen:
// VEVENTs mit DTSTART/DTEND, gefaltete Zeilen, TZID-Parameter, UTC-Zeiten.
//
// Bewusst übersprungen (mit Zähler gemeldet statt still verschluckt):
//  - Serientermine (RRULE) — Expansion wäre fehleranfällig, Praxen nutzen
//    sie für Patiententermine praktisch nie
//  - ganztägige Einträge (VALUE=DATE) — keine Uhrzeit, kein Behandlungsslot

const unescapeText = (s) =>
  s.replace(/\\n/gi, " · ").replace(/\\,/g, ",").replace(/\\;/g, ";").replace(/\\\\/g, "\\");

// "20260716T083000" / "...Z" → { date:"2026-07-16", time:"08:30", ms }
function parseDt(value) {
  const m = value.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})?(Z)?$/);
  if (!m) return null;
  const [, y, mo, d, h, mi, s, z] = m;
  let dt;
  if (z) dt = new Date(Date.UTC(+y, +mo - 1, +d, +h, +mi, +(s || 0)));
  else dt = new Date(+y, +mo - 1, +d, +h, +mi, +(s || 0)); // TZID/floating: als lokale Wanduhrzeit
  const pad = (n) => String(n).padStart(2, "0");
  return {
    date: `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`,
    time: `${pad(dt.getHours())}:${pad(dt.getMinutes())}`,
    ms: dt.getTime(),
  };
}

// "PT1H30M" → Minuten
function parseDuration(value) {
  const m = value.match(/^-?P(?:(\d+)D)?T?(?:(\d+)H)?(?:(\d+)M)?/);
  if (!m) return null;
  return (+(m[1] || 0)) * 1440 + (+(m[2] || 0)) * 60 + (+(m[3] || 0));
}

export function parseIcs(text) {
  if (!text.includes("BEGIN:VCALENDAR")) return null;
  const unfolded = text.replace(/\r?\n[ \t]/g, "");
  const lines = unfolded.split(/\r?\n/);

  const events = [];
  const skipped = { rrule: 0, allday: 0, invalid: 0 };
  let cur = null;

  for (const line of lines) {
    if (line === "BEGIN:VEVENT") { cur = {}; continue; }
    if (line === "END:VEVENT") {
      if (cur) {
        if (cur.RRULE) skipped.rrule++;
        else if (cur._allday) skipped.allday++;
        else if (!cur._start) skipped.invalid++;
        else {
          const start = cur._start;
          let dur = 30;
          if (cur._end && cur._end.ms > start.ms) dur = Math.round((cur._end.ms - start.ms) / 60000);
          else if (cur._duration) dur = cur._duration;
          events.push({
            uid: cur.UID || `${start.date}-${start.time}-${cur.SUMMARY || ""}`,
            date: start.date,
            time: start.time,
            dur: Math.min(Math.max(dur, 5), 600),
            summary: unescapeText(cur.SUMMARY || "Termin"),
            location: unescapeText(cur.LOCATION || ""),
            description: unescapeText(cur.DESCRIPTION || "").slice(0, 300),
          });
        }
      }
      cur = null; continue;
    }
    if (!cur) continue;

    const colon = line.indexOf(":");
    if (colon < 0) continue;
    const left = line.slice(0, colon);
    const value = line.slice(colon + 1);
    const [name, ...params] = left.split(";");

    if (name === "DTSTART") {
      if (params.some(p => p.toUpperCase() === "VALUE=DATE") || /^\d{8}$/.test(value)) cur._allday = true;
      else cur._start = parseDt(value);
    } else if (name === "DTEND") {
      if (!/^\d{8}$/.test(value)) cur._end = parseDt(value);
    } else if (name === "DURATION") {
      cur._duration = parseDuration(value);
    } else if (["SUMMARY", "LOCATION", "DESCRIPTION", "UID", "RRULE"].includes(name)) {
      cur[name] = value;
    }
  }
  return { events, skipped };
}

// Events in den dida-Terminbestand mergen (aptStore: DEMO localStorage, LIVE Supabase).
// Match-Reihenfolge: UID → (Datum+Zeit+Titel). Bestehende werden aktualisiert.
export async function mergeEventsIntoApts(events, { behandler, type }) {
  const apts = [...loadAptsCache()];
  let added = 0, updated = 0;
  events.forEach((ev, i) => {
    const patch = { date: ev.date, time: ev.time, dur: ev.dur, patient: ev.summary, anliegen: ev.description || ev.location || "Kalender-Import" };
    let hit = apts.find(a => a.icsUid && a.icsUid === ev.uid);
    if (!hit) hit = apts.find(a => a.date === ev.date && a.time === ev.time && a.patient === ev.summary);
    if (hit) {
      Object.assign(hit, patch, { icsUid: ev.uid });
      updated++;
    } else {
      apts.push({
        id: Date.now() + i, icsUid: ev.uid, ...patch,
        dob: "", allergy: "", behandler, type,
        status: "gebucht", src: "Kalender", kv: "", notes: "", room: "", waitSince: null,
      });
      added++;
    }
  });
  await saveAllApts(apts);
  return { added, updated };
}
